import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "shared/contexts/I18nContext";
import { useLoading } from "shared/contexts/Loading";
import { useSubscription } from "shared/contexts/Subscription";
import ContentHeaderSection from "../../../shared/common/ContentHeaderSection";
import { useMasterList } from "shared/hooks/useMasterList";
import apiCall from "shared/services/ApiAdapter";
import { getCurrentUserId } from "shared/utils/SessionDataUtils";

const CreateUpdateStudentForm = ({ navigateTo, studentId }) => {
  const { t } = useTranslation();
  const { canPerformAction } = useSubscription();
  const students = []; // Assumes a list of students is available or fetched elsewhere

  const [student, setStudent] = useState({
    name: "",
    email: "",
    gender: "",
    curriculum: "",
    academicClass: "",
    schoolName: "",
    parentEmail: "",
    parentPhone: "",
    hasLearningDisability: false,
    isActive: true,
    profilePictureUrl: "",
    dateOfBirth: "",
    enrollmentDate: "",
  });

  const [errors, setErrors] = useState({});
  const [formLoading, setFormLoading] = useState(false);
  const [declarationAccepted, setDeclarationAccepted] = useState(false);
  const useMockData = false;
  const currentUserId = getCurrentUserId();

  // The useLoading hook should be from your context
  const { startLoading, stopLoading } = useLoading();

  const displayToast = useCallback((message, type) => {
    // This function should be implemented by your application's toast service
    console.log(`Toast (${type}): ${message}`);
  }, []);

  // The useMasterList hook is used as provided in your example
  const {
    masterData: { curricula, academicClasses },
    isLoading: masterDataLoading,
    error: masterDataError,
  } = useMasterList({ displayToast, startLoading, stopLoading });

  // Normalize various inputs (id/object/code) into a code using master lists
  const resolveToCode = useCallback((list, valueOrObject) => {
    if (!list || !Array.isArray(list)) return "";
    if (valueOrObject == null) return "";
    if (typeof valueOrObject === "string") return valueOrObject; // already code
    if (typeof valueOrObject === "number") {
      // treat as id
      const byId = list.find((it) => it.id === valueOrObject);
      return byId ? byId.value : "";
    }
    if (typeof valueOrObject === "object") {
      if (valueOrObject.code) return valueOrObject.code;
      if (valueOrObject.value) return valueOrObject.value;
      if (valueOrObject.id != null) {
        const byId = list.find((it) => it.id === valueOrObject.id);
        return byId ? byId.value : "";
      }
    }
    return "";
  }, []);

  useEffect(() => {
    const fetchFormData = async () => {
      if (studentId) {
        console.log(
          `[CreateUpdateStudentForm] Fetching student details for ID: ${studentId}`
        );
        setFormLoading(true);
        try {
          const existingStudent = await apiCall(
            `/students/${studentId}`,
            useMockData,
            "GET",
            null,
            startLoading,
            stopLoading,
            displayToast
          );
          console.log(
            `[CreateUpdateStudentForm] Result of apiCall for student details:`,
            existingStudent
          );

          if (existingStudent) {
            setStudent({
              ...existingStudent,
              dateOfBirth: existingStudent.dateOfBirth
                ? new Date(existingStudent.dateOfBirth)
                    .toISOString()
                    .split("T")[0]
                : "",
              enrollmentDate: existingStudent.enrollmentDate
                ? new Date(existingStudent.enrollmentDate)
                    .toISOString()
                    .split("T")[0]
                : "",
              parentEmail: existingStudent.parentEmail || "",
              parentPhone: existingStudent.parentPhone || "",
              // Normalize to codes
              curriculum: resolveToCode(
                curricula,
                existingStudent.curriculum ??
                  existingStudent.curriculumId ??
                  existingStudent.curriculumCode
              ),
              academicClass: resolveToCode(
                academicClasses,
                existingStudent.academicClass ??
                  existingStudent.classStandardId ??
                  existingStudent.classCode
              ),
              schoolName: existingStudent.schoolName || "",
              hasLearningDisability:
                existingStudent.hasLearningDisability || false,
              isActive: existingStudent.isActive || true,
            });
            setDeclarationAccepted(true);
          } else {
            displayToast(`Student with ID ${studentId} not found.`, "error");
          }
        } catch (error) {
          displayToast(`Failed to load form data: ${error.message}`, "error");
        } finally {
          setFormLoading(false);
        }
      } else {
        console.log(
          "[CreateUpdateStudentForm] Creating new student - form cleared."
        );
        setStudent({
          name: "",
          email: "",
          gender: "",
          curriculum: "",
          academicClass: "",
          schoolName: "",
          parentEmail: "",
          parentPhone: "",
          hasLearningDisability: false,
          isActive: true,
          profilePictureUrl: "",
          dateOfBirth: "",
          enrollmentDate: "",
        });
        setDeclarationAccepted(false);
      }
    };

    if (!masterDataLoading) {
      fetchFormData();
    }
  }, [studentId, displayToast, startLoading, stopLoading, masterDataLoading]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStudent((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDeclarationChange = (e) => {
    setDeclarationAccepted(e.target.checked);
    setErrors((prev) => ({ ...prev, declaration: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!student.name.trim()) newErrors.name = t("requiredField");
    if (!student.email.trim()) newErrors.email = t("requiredField");
    if (!student.gender) newErrors.gender = t("requiredField");
    if (!student.curriculum) newErrors.curriculum = t("requiredField");
    if (!student.academicClass) newErrors.academicClass = t("requiredField");
    if (!declarationAccepted) newErrors.declaration = t("declarationRequired");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      displayToast(t("formErrors"), "error");
      return;
    }

    setFormLoading(true);

    if (!studentId && !canPerformAction("maxStudents", students.length)) {
      displayToast(`Cannot add new student. ${t("limitReached")}`, "error");
      setFormLoading(false);
      return;
    }

    const studentToSave = {
      ...student,
      // Ensure codes are sent
      curriculum: resolveToCode(curricula, student.curriculum),
      academicClass: resolveToCode(academicClasses, student.academicClass),
      dateOfBirth: student.dateOfBirth
        ? new Date(student.dateOfBirth).toISOString()
        : null,
      enrollmentDate: student.enrollmentDate
        ? new Date(student.enrollmentDate).toISOString()
        : null,
      hasLearningDisability: Boolean(student.hasLearningDisability),
      isActive: Boolean(student.isActive),
    };

    const now = new Date().toISOString();

    try {
      if (studentId) {
        const payload = {
          ...studentToSave,
          id: studentId,
          updatedBy: currentUserId,
          updatedDate: now,
        };
        await apiCall(
          `/students/${studentId}`,
          useMockData,
          "PUT",
          payload,
          startLoading,
          stopLoading,
          displayToast
        );
      } else {
        const payload = {
          ...studentToSave,
          createdBy: currentUserId,
          updatedBy: currentUserId,
          createdDate: now,
          updatedDate: now,
        };
        await apiCall(
          `/students`,
          useMockData,
          "POST",
          payload,
          startLoading,
          stopLoading,
          displayToast
        );
      }
      displayToast("Student saved successfully!", "success");
      navigateTo("students");
    } catch (error) {
      console.error("Failed to save student:", error);
      displayToast(`Failed to save student: ${error.message}`, "error");
    } finally {
      setFormLoading(false);
    }
  };

  const overallLoading = formLoading || masterDataLoading;

  return (
    <div className="p-8 md:px-10">
      <ContentHeaderSection
        title={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigateTo("students")}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
              aria-label="Go back to students"
            >
              <svg
                className="w-6 h-6 text-gray-600 dark:text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>
            <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight leading-tight">
              {studentId ? t("updateStudent") : t("addStudentTitle")}
            </span>
          </div>
        }
        subtitle={t("addStudentSubtitle")}
      />
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 max-w-2xl mx-auto">
        {overallLoading ? (
          <div className="text-center text-gray-600 dark:text-gray-400">
            {t("loadingStudentDetails")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("studentName")}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={student.name}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                  errors.name
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("studentEmail")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={student.email}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 ${
                  errors.email
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="gender"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("gender")}
              </label>
              <select
                id="gender"
                name="gender"
                value={student.gender}
                onChange={handleChange}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                  errors.gender
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                <option value="">{t("select")}</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="curriculum"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("curriculum")}
              </label>
              <select
                id="curriculum"
                name="curriculum"
                value={student.curriculum}
                onChange={handleChange}
                disabled={masterDataLoading}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                  errors.curriculum
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {curricula.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.curriculum && (
                <p className="text-red-500 text-sm mt-1">{errors.curriculum}</p>
              )}
              {masterDataError && (
                <p className="text-red-500 text-sm mt-1">{masterDataError}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="academicClass"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("class")}
              </label>
              <select
                id="academicClass"
                name="academicClass"
                value={student.academicClass}
                onChange={handleChange}
                disabled={masterDataLoading}
                className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:text-gray-100 ${
                  errors.academicClass
                    ? "border-red-500"
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                <option value="">
                  {masterDataLoading ? t("loading") : t("select")}
                </option>
                {academicClasses.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
              {errors.academicClass && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.academicClass}
                </p>
              )}
              {masterDataError && (
                <p className="text-red-500 text-sm mt-1">{masterDataError}</p>
              )}
            </div>
            <div>
              <label
                htmlFor="schoolName"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("schoolName")} (Optional)
              </label>
              <input
                type="text"
                id="schoolName"
                name="schoolName"
                value={student.schoolName}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div>
              <label
                htmlFor="parentEmail"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("parentEmail")}
              </label>
              <input
                type="email"
                id="parentEmail"
                name="parentEmail"
                value={student.parentEmail}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <div>
              <label
                htmlFor="parentPhone"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                {t("parentPhone")} (Optional)
              </label>
              <input
                type="text"
                id="parentPhone"
                name="parentPhone"
                value={student.parentPhone}
                onChange={handleChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              />
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="declaration"
                  name="declaration"
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={handleDeclarationChange}
                  className={`h-4 w-4 rounded dark:bg-gray-700 dark:border-gray-600 ${
                    errors.declaration
                      ? "border-red-500"
                      : "border-gray-300 text-green-600 focus:ring-green-500"
                  }`}
                />
              </div>
              <div className="ml-3 text-sm">
                <label
                  htmlFor="declaration"
                  className="font-medium text-gray-700 dark:text-gray-300"
                >
                  I hereby declare that the above information is true and
                  correct to the best of my knowledge.
                </label>
              </div>
            </div>
            {errors.declaration && (
              <p className="text-red-500 text-sm mt-1">{errors.declaration}</p>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => navigateTo("students")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                {t("cancel")}
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                disabled={overallLoading}
              >
                {overallLoading
                  ? studentId
                    ? t("updating")
                    : t("creating")
                  : studentId
                  ? t("update")
                  : t("create")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
export default CreateUpdateStudentForm;
